/**
 * Square Web Payments — client-side configuration and helpers.
 *
 * SECURITY BOUNDARY: only the Application ID and Location ID live on the client.
 * Both are public by design — they ship in the browser bundle. The Square
 * *access token* is a secret and NEVER touches the client; it lives only in the
 * Cloudflare Pages Function environment (see functions/api/create-payment.js).
 *
 * Card data is handled entirely inside Square's hosted iframe (the Web Payments
 * SDK), so a raw card number never enters our React state or our own DOM — the
 * browser only ever sees a single-use token.
 */

export const SQUARE_ENVIRONMENT = (
  process.env.REACT_APP_SQUARE_ENVIRONMENT || 'sandbox'
).toLowerCase();

export const SQUARE_APPLICATION_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID || '';
export const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID || '';

/**
 * True only when the public credentials are present. When false the checkout
 * transparently falls back to the built-in demo flow, so the site keeps working
 * before Square is set up.
 */
export const isSquareConfigured = () =>
  Boolean(SQUARE_APPLICATION_ID && SQUARE_LOCATION_ID);

const SDK_URL =
  SQUARE_ENVIRONMENT === 'production'
    ? 'https://web.squarecdn.com/v1/square.js'
    : 'https://sandbox.web.squarecdn.com/v1/square.js';

let sdkPromise = null;

/** Load the Web Payments SDK once, on demand. Resolves with `window.Square`. */
export const loadSquareSdk = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('The Square SDK can only load in the browser.'));
  }
  if (window.Square) return Promise.resolve(window.Square);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.onload = () =>
      window.Square
        ? resolve(window.Square)
        : reject(new Error('Square SDK loaded but window.Square is unavailable.'));
    script.onerror = () => {
      sdkPromise = null; // allow a later retry
      reject(new Error('Could not load the Square payment library.'));
    };
    document.head.appendChild(script);
  });
  return sdkPromise;
};

/**
 * Hand a tokenized card to our Cloudflare Pages Function, which performs the
 * actual charge with the secret access token. The price is (re)computed
 * server-side from the plot ids — the browser never names its own amount.
 *
 * @returns {Promise<{ok:true, paymentId:string, status:string, receiptUrl?:string, amount:number, currency:string}>}
 */
export const createSquarePayment = async (payload) => {
  let res;
  try {
    res = await fetch('/api/create-payment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new Error('Could not reach the payment service. Please try again.');
  }

  let data;
  try {
    data = await res.json();
  } catch {
    // Almost always means the Pages Function isn't running (e.g. plain
    // `npm start` instead of `wrangler pages dev`).
    throw new Error('The payment service is unavailable in this environment.');
  }

  if (!res.ok || !data.ok) {
    throw new Error(data.error || 'The payment could not be completed.');
  }
  return data;
};
